import { action, computed, makeObservable, observable } from 'mobx';
import { Aggregate } from 'edit/Aggregate';
import type { EditableEntity } from 'edit/EditableEntity';
import type { NodeDefinition } from 'api/node_definitions/endpoints/getNodeDefinition';
import type { OutputMatcher, Parameter, RaiseEvent, RetentionPolicy, SaveNodeDefinition, WaitEvent } from 'api/node_definitions/endpoints/types';
import { EditableHandle, type Handle } from './EditableHandle';
import { EditableOutputMatcher } from './EditableOutputMatcher';
import { EditableParameter } from './EditableParameter';
import { EditableRaiseEvent } from './EditableRaiseEvent';
import { EditableRetentionPolicy } from 'edit/retentionPolicy/EditableRetentionPolicy';
import { EditableWaitEvent } from './EditableWaitEvent';
import { buildAutoRestartView, type AutoRestartView } from './autoRestartView';
import { normalizeChildEdgesForSave, normalizeForComparison } from './workingState';

export interface EditableNodeDefinitionOptions {
  /** Pre-fill from a loaded record but treat it as a new record (POST, cleared name). */
  readonly duplicate?: boolean;
}

/**
 * The editor's working state — the snake-case, react-flow-shaped object the existing tab and Flow components consume via
 * their `state`/`setState` props. The model projects this from its typed fields (plus a passthrough bag for transient
 * UI-only keys like `selectedNode`), so those components keep working unchanged while the model owns the state. The index
 * signature carries the passthrough keys; the named fields are what the model owns.
 */
export interface NodeEditorState {
  readonly [key: string]: unknown;
  readonly node_definition_id: number | string;
  readonly version: number | undefined;
  readonly editable_handles: boolean;
  readonly name: string | null;
  readonly description: string | null;
  readonly type: string | null;
  readonly sub_type: string | null;
  readonly zone_name: string | null;
  readonly folder: string | null;
  readonly folder_name: string | null;
  readonly default_queue_path: string | null;
  readonly documentation: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  readonly runtime_limit_min_hhmmss: string | null;
  readonly runtime_limit_max_hhmmss: string | null;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
  readonly locks: readonly string[];
  readonly retention_policies: readonly RetentionPolicy[];
  readonly output_matchers: readonly OutputMatcher[];
}

// Keys the model owns and projects from its typed fields; everything else on an incoming state is transient UI (kept in the
// passthrough bag). Read-only identity/meta (node_definition_id, version, editable_handles) is exposed but never written back.
const OWNED_STATE_KEYS: ReadonlySet<string> = new Set([
  'node_definition_id',
  'version',
  'editable_handles',
  'name',
  'description',
  'type',
  'sub_type',
  'zone_name',
  'folder',
  'folder_name',
  'default_queue_path',
  'documentation',
  'restrict_to_scheduling_window',
  'auto_restart_enabled',
  'auto_restart_amount',
  'auto_restart_delay_seconds',
  'restart_on_return_codes',
  'hold_on_submit',
  'runtime_limit_min_hhmmss',
  'runtime_limit_max_hhmmss',
  'runtime_limit_event',
  'runtime_limit_terminate',
  'config',
  'handles',
  'parameters',
  'wait_events',
  'raise_events',
  'child_nodes',
  'child_edges',
  'locks',
  'retention_policies',
  'output_matchers',
]);

// Empty string / null / non-finite -> null, otherwise the number. Bridged number-input tabs emit strings; the backend and
// the model's typed fields want a number or null.
const toIntOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// Coerce to a non-negative number (auto-restart counts / delays), defaulting to 0.
const toNonNegative = (value: unknown): number => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
};

const extractPassthrough = (next: Record<string, unknown>): Record<string, unknown> => {
  const bag: Record<string, unknown> = {};
  for (const key of Object.keys(next)) {
    if (!OWNED_STATE_KEYS.has(key)) bag[key] = next[key];
  }
  return bag;
};

/**
 * A node definition open for editing. Built directly from the loaded wire record (or `null` for a blank create form) — there
 * is no intermediate "editable data" type; the model *is* the editable state, and `toSave` projects straight back to the
 * request body.
 *
 * User-fillable fields are public observables — read `model.name`, write `model.name = x` (and `setState` routes bridged
 * writes back into them). Each repeated collection is an `Aggregate` of child editables edited natively by its own tab
 * (`outputMatchers`, `retentionPolicies`, `handles`, `waitEvents`, `raiseEvents`, `parameters`). `config`, `childNodes` and
 * `childEdges` are opaque (`unknown`) — round-tripped verbatim; the Flow bridge is responsible for stripping react-flow UI
 * state before writing the graph back.
 *
 * `original` is the pre-edit identity used to choose POST vs PUT, to address the PUT URL, and to carry the `if_match_version`
 * optimistic-concurrency token; `null` means a not-yet-saved record. It (and the other internal-write fields exposed
 * read-only — `originalParameterNames`, `editableHandles`) is written only via `_init`/`_applyState`.
 *
 * `isDirty` is "the current persisted state differs from the snapshot captured at load/reinitialize" — so reverting an edit
 * by hand reads clean again, and an untouched create form reads clean (no spurious unsaved-changes prompt on close).
 */
export class EditableNodeDefinition implements EditableEntity<NodeDefinition | null> {
  autoRestartAmount: number = 0;
  autoRestartDelaySeconds: number = 0;
  autoRestartEnabled: boolean = false;
  childEdges: readonly unknown[] = [];
  childNodes: readonly unknown[] = [];
  config: unknown = {};
  defaultQueuePath: string | null = null;
  description: string | null = null;
  documentation: string | null = null;
  /** Read-only capability flag from the node type; surfaced so the (bridged) handles editor can gate itself. */
  private _editableHandles: boolean = false;
  folderPath: string | null = null;
  // An ordered Aggregate like _outputMatchers: the Handles editor edits it natively (add/remove/reorder). It leaves the
  // state/setState bridge (applyState skips it) but is still projected into `state` so handle edits count toward isDirty.
  private readonly _handles = new Aggregate<EditableNodeDefinition, EditableHandle>(this, (aggregate) => new EditableHandle(aggregate));
  holdOnSubmit: boolean = false;
  locks: readonly string[] = [];
  name: string | null = null;
  private _original: { nodeDefinitionId: number; zoneName: string; name: string; version: number } | null = null;
  private _originalParameterNames: readonly string[] = [];
  // The first ordered Aggregate in this editor: the matcher tab edits it natively (add/remove/reorder). It leaves the
  // state/setState bridge (applyState skips it) but is still projected into `state` so matcher edits count toward isDirty.
  private readonly _outputMatchers = new Aggregate<EditableNodeDefinition, EditableOutputMatcher>(this, (aggregate) => new EditableOutputMatcher(aggregate));
  private readonly _parameters = new Aggregate<EditableNodeDefinition, EditableParameter>(this, (aggregate) => new EditableParameter(aggregate));
  private readonly _raiseEvents = new Aggregate<EditableNodeDefinition, EditableRaiseEvent>(this, (aggregate) => new EditableRaiseEvent(aggregate));
  restartOnReturnCodes: readonly number[] = [];
  restrictToSchedulingWindow: number | null = null;
  private readonly _retentionPolicies = new Aggregate<EditableNodeDefinition, EditableRetentionPolicy>(
    this,
    (aggregate) => new EditableRetentionPolicy(aggregate)
  );
  runtimeLimitEvent: number | null = null;
  runtimeLimitMaxHhmmss: string | null = null;
  runtimeLimitMinHhmmss: string | null = null;
  runtimeLimitTerminate: boolean = false;
  private _snapshot = '';
  subType: string | null = null;
  type: string | null = null;
  /** Transient UI-only keys the tabs/Flow carry on the state but the model doesn't own (e.g. `selectedNode`). */
  private _ui: Record<string, unknown> = {};
  private readonly _waitEvents = new Aggregate<EditableNodeDefinition, EditableWaitEvent>(this, (aggregate) => new EditableWaitEvent(aggregate));
  private _zoneName: string | null = null;

  constructor(loaded: NodeDefinition | null, options?: EditableNodeDefinitionOptions) {
    makeObservable<EditableNodeDefinition, '_editableHandles' | '_original' | '_originalParameterNames' | '_ui' | '_zoneName'>(this, {
      autoRestartAmount: observable,
      autoRestartDelaySeconds: observable,
      autoRestartEnabled: observable,
      childEdges: observable.ref,
      childNodes: observable.ref,
      config: observable.ref,
      defaultQueuePath: observable,
      description: observable,
      documentation: observable,
      _editableHandles: observable,
      folderPath: observable,
      holdOnSubmit: observable,
      locks: observable.ref,
      name: observable,
      _original: observable.ref,
      _originalParameterNames: observable.ref,
      restartOnReturnCodes: observable.ref,
      restrictToSchedulingWindow: observable,
      runtimeLimitEvent: observable,
      runtimeLimitMaxHhmmss: observable,
      runtimeLimitMinHhmmss: observable,
      runtimeLimitTerminate: observable,
      subType: observable,
      type: observable,
      _ui: observable.ref,
      _zoneName: observable,
      autoRestartView: computed,
      isDirty: computed,
    });
    this._init(loaded, options?.duplicate ?? false);
  }

  /**
   * Derived live summary + retry timeline for the Properties-tab card; `null` when auto-restart is off. A computed so the
   * component stays a pure observer: it reads `autoRestartView` and renders it, with no in-component derivation.
   */
  get autoRestartView(): AutoRestartView | null {
    return this.autoRestartEnabled
      ? buildAutoRestartView({
          amount: this.autoRestartAmount,
          delaySeconds: this.autoRestartDelaySeconds,
          codes: this.restartOnReturnCodes,
        })
      : null;
  }

  /** Read-only: whether this node type allows editing handles. */
  get editableHandles(): boolean {
    return this._editableHandles;
  }

  get handles(): readonly EditableHandle[] {
    return this._handles.children;
  }

  get isCreating(): boolean {
    return this._original === null;
  }

  get isDirty(): boolean {
    return this._persistedSnapshot() !== this._snapshot;
  }

  /**
   * Pre-edit identity (`null` while creating). The `save` callback reads this to choose create vs update, to build the PUT
   * URL, and to send `if_match_version`.
   */
  get original(): { nodeDefinitionId: number; zoneName: string; name: string; version: number } | null {
    return this._original;
  }

  /**
   * Parameter names present in the last-loaded/saved baseline. Used to detect parameters removed during this edit (so the
   * host can warn when a removed parameter is still referenced by flows). Empty while creating or duplicating.
   */
  get originalParameterNames(): readonly string[] {
    return this._originalParameterNames;
  }

  get outputMatchers(): readonly EditableOutputMatcher[] {
    return this._outputMatchers.children;
  }

  get parameters(): readonly EditableParameter[] {
    return this._parameters.children;
  }

  /**
   * Owner role/user live in `config` (the backend reads `config.owner_role` / `config.owner_user` when raising an operator
   * message on failure), but the UI surfaces them for every node type — so they're exposed as first-class fields that
   * read/write the config bag rather than another editor bridging `config` directly. The stored id is a string (the config
   * has always held the picker's option value); empty/absent reads as `null`. The setter merges to preserve the type-specific
   * config keys (e.g. `script`, the flow graph), and drops the key entirely when cleared so a saved config carries no empty owner.
   */
  get ownerRole(): string | null {
    const v = (this.config as { owner_role?: unknown } | null)?.owner_role;
    return v == null || v === '' ? null : String(v);
  }
  set ownerRole(value: string | null) {
    this.config = { ...((this.config as Record<string, unknown> | null) ?? {}), owner_role: value || undefined };
  }

  get ownerUser(): string | null {
    const v = (this.config as { owner_user?: unknown } | null)?.owner_user;
    return v == null || v === '' ? null : String(v);
  }
  set ownerUser(value: string | null) {
    this.config = { ...((this.config as Record<string, unknown> | null) ?? {}), owner_user: value || undefined };
  }

  get raiseEvents(): readonly EditableRaiseEvent[] {
    return this._raiseEvents.children;
  }

  get retentionPolicies(): readonly EditableRetentionPolicy[] {
    return this._retentionPolicies.children;
  }

  /**
   * The editor's working state — the shape the (un-migrated) tab and Flow components read via their `state` prop. Projected
   * fresh from the typed fields plus the transient passthrough bag; read-only identity/meta is included so consumers that
   * read `node_definition_id` / `version` / `editable_handles` keep working. Paired with `setState` for the write direction.
   */
  get state(): NodeEditorState {
    const original = this._original;
    return {
      ...this._ui,
      node_definition_id: original?.nodeDefinitionId ?? '',
      version: original?.version,
      editable_handles: this._editableHandles,
      name: this.name,
      description: this.description,
      type: this.type,
      sub_type: this.subType,
      zone_name: this.zoneName,
      // Both aliases resolve to the same folder path (the Definition tab reads `folder ?? folder_name`, writes `folder`).
      folder: this.folderPath,
      folder_name: this.folderPath,
      default_queue_path: this.defaultQueuePath,
      documentation: this.documentation,
      restrict_to_scheduling_window: this.restrictToSchedulingWindow,
      auto_restart_enabled: this.autoRestartEnabled,
      auto_restart_amount: this.autoRestartAmount,
      auto_restart_delay_seconds: this.autoRestartDelaySeconds,
      restart_on_return_codes: this.restartOnReturnCodes,
      hold_on_submit: this.holdOnSubmit,
      runtime_limit_min_hhmmss: this.runtimeLimitMinHhmmss,
      runtime_limit_max_hhmmss: this.runtimeLimitMaxHhmmss,
      runtime_limit_event: this.runtimeLimitEvent,
      runtime_limit_terminate: this.runtimeLimitTerminate,
      config: this.config,
      handles: this._handles.children.map((h) => h.toSave()),
      parameters: this._parameters.children.map((p, index) => p.toSave(index + 1)),
      wait_events: this._waitEvents.children.map((w) => w.toSave()),
      raise_events: this._raiseEvents.children.map((r) => r.toSave()),
      child_nodes: this.childNodes,
      child_edges: this.childEdges,
      locks: this.locks,
      retention_policies: this._retentionPolicies.children.map((p) => p.toSave()),
      output_matchers: this._outputMatchers.children.map((m) => m.toSave()),
    };
  }

  get waitEvents(): readonly EditableWaitEvent[] {
    return this._waitEvents.children;
  }

  get zoneName(): string | null {
    return this._zoneName;
  }
  /**
   * Changing the zone clears the folder — folders are zone-scoped, so a folder chosen under the old zone is no longer valid.
   * Only user-facing sets go through here; `_init`/`_applyState` assign `_zoneName` directly to bypass the clear (they set
   * the folder themselves from the loaded/incoming state).
   */
  set zoneName(next: string | null) {
    if (next !== this._zoneName) this.folderPath = null;
    this._zoneName = next;
  }

  /** Append a fresh, empty handle and return it. */
  createHandle = (): EditableHandle => this._handles.create();

  /** Append a fresh, empty matcher and return it. */
  createOutputMatcher = (): EditableOutputMatcher => this._outputMatchers.create();

  /** Append a fresh parameter and return it. */
  createParameter = (): EditableParameter => this._parameters.create();

  /** Append a fresh, empty raise event and return it. */
  createRaiseEvent = (): EditableRaiseEvent => this._raiseEvents.create();

  /** Append a fresh wait event (auto-submit default follows the definition's default queue) and return it. */
  createWaitEvent = (): EditableWaitEvent => this._waitEvents.create();

  /** Append a fresh, empty retention policy and return it. */
  createRetentionPolicy = (): EditableRetentionPolicy => this._retentionPolicies.create();

  /**
   * Reset to the loaded record (or a blank create form when `null`) and recapture the dirty baseline. Called after a
   * successful load or save.
   */
  reinitialize = (loaded: NodeDefinition | null): void => {
    this._init(loaded);
  };

  /** Reorder handles (final-resting-index contract; wired to the handle list's drag reorder). */
  reorderHandles = (fromIndex: number, toIndex: number): void => this._handles.move(fromIndex, toIndex);

  /** Reorder parameters (final-resting-index contract; array position is the persisted order). */
  reorderParameters = (fromIndex: number, toIndex: number): void => this._parameters.move(fromIndex, toIndex);

  /** Reorder matchers (final-resting-index contract; wired to the matcher list's drag reorder). */
  reorderOutputMatchers = (fromIndex: number, toIndex: number): void => this._outputMatchers.move(fromIndex, toIndex);

  /**
   * React-style setter handed to the un-migrated tab and Flow components as their `setState` prop. Accepts either a next
   * state or an updater `(prev) => next` (the form those components use), then routes each key back into the typed fields
   * (with UI->model coercion) or the transient passthrough bag. Owned read-only keys are ignored.
   */
  setState = (updater: NodeEditorState | ((prev: NodeEditorState) => NodeEditorState)): void => {
    const next = typeof updater === 'function' ? updater(this.state) : updater;
    this._applyState(next);
  };

  /**
   * Project to the request body. Snake-case wire names; the same body serves POST and PUT. `if_match_version` is included
   * only in edit mode (the loaded `version`); on create it is omitted so the backend skips the concurrency check. Order of
   * ordered collections is conveyed by array position.
   */
  toSave(): SaveNodeDefinition {
    const original = this._original;
    const type = this.type;
    // Each child-node placement carries the child definition's name + zone so the backend can resolve its id.
    const childNodes = (this.childNodes as { name?: string; node_definition_name?: string; zone_name?: string }[]).map((node) => ({
      ...node,
      node_definition_name: node.node_definition_name || node.name,
      zone_name: node.zone_name || (this.zoneName ?? undefined),
    }));
    return {
      name: this.name,
      description: this.description,
      type,
      // Shell nodes carry a shell flavour (default bash); every other type has no sub_type.
      sub_type: type === 'shell' ? this.subType || 'bash' : null,
      zone_name: this.zoneName,
      folder_name: this.folderPath,
      default_queue_path: this.defaultQueuePath,
      restrict_to_scheduling_window: this.restrictToSchedulingWindow,
      config: this.config,
      handles: this._handles.children.map((h) => h.toSave()),
      parameters: this._parameters.children.map((p, index) => p.toSave(index + 1)),
      wait_events: this._waitEvents.children.map((w) => w.toSave()),
      raise_events: this._raiseEvents.children.map((r) => r.toSave()),
      child_nodes: childNodes,
      child_edges: normalizeChildEdgesForSave(this.childEdges),
      locks: this.locks,
      retention_policies: this._retentionPolicies.children.map((p) => p.toSave()),
      output_matchers: this._outputMatchers.children.map((m) => m.toSave()),
      auto_restart_enabled: this.autoRestartEnabled,
      auto_restart_amount: toNonNegative(this.autoRestartAmount),
      auto_restart_delay_seconds: toNonNegative(this.autoRestartDelaySeconds),
      restart_on_return_codes: this.restartOnReturnCodes,
      hold_on_submit: this.holdOnSubmit,
      runtime_limit_min_hhmmss: this.runtimeLimitMinHhmmss,
      runtime_limit_max_hhmmss: this.runtimeLimitMaxHhmmss,
      runtime_limit_event: this.runtimeLimitEvent,
      runtime_limit_terminate: this.runtimeLimitTerminate,
      documentation: this.documentation,
      ...(original !== null ? { if_match_version: original.version } : {}),
    };
  }

  // Writes a full working state back into the typed fields (with UI->model coercion) and stashes the unowned keys as
  // transient passthrough. Owned read-only keys (identity/meta) are not written back.
  //
  // Wrapped in `action`: this is the interaction hot path (every keystroke / graph change flows through it) and mutates ~30
  // observables. Without batching each write would end its own implicit transaction and fire reactions separately, so a
  // single edit would recompute `state`/`isDirty` and re-render every observer many times, some against a half-applied state.
  // (This is the deliberate exception to the codebase's "no action() by default" rule, which targets async methods where
  // action only covers the sync prelude — here the whole method is synchronous, so the transaction covers all of it.)
  private readonly _applyState = action((next: NodeEditorState): void => {
    this.name = next.name ?? null;
    this.description = next.description ?? null;
    this.type = next.type ?? null;
    this.subType = next.sub_type ?? null;
    this._zoneName = next.zone_name ?? null;
    this.folderPath = next.folder ?? next.folder_name ?? null;
    this.defaultQueuePath = next.default_queue_path ?? null;
    this.documentation = next.documentation ?? null;
    this.restrictToSchedulingWindow = toIntOrNull(next.restrict_to_scheduling_window);
    this.autoRestartEnabled = !!next.auto_restart_enabled;
    // Stored raw (not coerced here) so a free-text number field can be blanked mid-edit; toSave() clamps to a number.
    this.autoRestartAmount = next.auto_restart_amount;
    this.autoRestartDelaySeconds = next.auto_restart_delay_seconds;
    this.restartOnReturnCodes = next.restart_on_return_codes ?? [];
    this.holdOnSubmit = !!next.hold_on_submit;
    this.runtimeLimitMinHhmmss = next.runtime_limit_min_hhmmss ?? null;
    this.runtimeLimitMaxHhmmss = next.runtime_limit_max_hhmmss ?? null;
    this.runtimeLimitEvent = toIntOrNull(next.runtime_limit_event);
    this.runtimeLimitTerminate = !!next.runtime_limit_terminate;
    this.config = next.config ?? {};
    this.childNodes = next.child_nodes ?? [];
    this.childEdges = next.child_edges ?? [];
    this.locks = next.locks ?? [];
    // parameters, handles, wait_events, raise_events, retention_policies and output_matchers are intentionally not written
    // here: they are Aggregates edited natively by their own editors. They stay in OWNED_STATE_KEYS (so they aren't dumped to
    // the passthrough bag) and are still projected into `state` for dirty tracking.

    const bag = extractPassthrough(next);
    if (JSON.stringify(bag) !== JSON.stringify(this._ui)) this._ui = bag;
  });

  // The dirty baseline: the persisted content of the working state, normalized so react-flow UI noise (selection, drag,
  // measured geometry) doesn't read as an edit.
  private _persistedSnapshot(): string {
    return JSON.stringify(normalizeForComparison(this.state));
  }

  // Shared by the constructor and `reinitialize`. Populates every field from the loaded record (or resets to create-form
  // defaults when `null`) and snapshots the resulting persisted state for dirty tracking.
  // Batched (action) for the same reason as _applyState: a synchronous bulk write of ~30 observables, and reinitialize runs
  // after a save while the editor is mounted and observed.
  private readonly _init = action((loaded: NodeDefinition | null, duplicate = false): void => {
    this._ui = {};
    if (loaded !== null) {
      if (duplicate) {
        this._original = null;
        this.name = null;
      } else {
        this._original = { nodeDefinitionId: loaded.node_definition_id, zoneName: loaded.zone_name, name: loaded.name, version: loaded.version };
        this.name = loaded.name;
      }
      this.description = loaded.description;
      this.type = loaded.type;
      this.subType = loaded.sub_type;
      this._zoneName = loaded.zone_name;
      this.folderPath = loaded.folder_name;
      this.defaultQueuePath = loaded.default_queue_path;
      this.documentation = loaded.documentation;
      this._editableHandles = loaded.editable_handles;
      this.restrictToSchedulingWindow = loaded.restrict_to_scheduling_window;
      this.autoRestartEnabled = loaded.auto_restart_enabled;
      this.autoRestartAmount = loaded.auto_restart_amount;
      this.autoRestartDelaySeconds = loaded.auto_restart_delay_seconds;
      this.restartOnReturnCodes = loaded.restart_on_return_codes;
      this.holdOnSubmit = loaded.hold_on_submit;
      this.runtimeLimitMinHhmmss = loaded.runtime_limit_min_hhmmss ?? null;
      this.runtimeLimitMaxHhmmss = loaded.runtime_limit_max_hhmmss ?? null;
      this.runtimeLimitEvent = loaded.runtime_limit_event;
      this.runtimeLimitTerminate = loaded.runtime_limit_terminate;
      this.config = loaded.config;
      this._parameters.clear();
      // Array position is the persisted order, so seed children in `order` (nulls last) — after this, index === order.
      const orderedParameters = [...loaded.parameters].sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
      for (const wire of orderedParameters) {
        this._parameters.append(new EditableParameter(this._parameters, wire));
      }
      // A duplicate is a new record (POST) with no server baseline to check removed-parameter usage against.
      this._originalParameterNames = duplicate ? [] : loaded.parameters.map((p) => p.name);
      this._waitEvents.clear();
      for (const wire of loaded.wait_events) {
        this._waitEvents.append(new EditableWaitEvent(this._waitEvents, wire));
      }
      this._raiseEvents.clear();
      for (const wire of loaded.raise_events) {
        this._raiseEvents.append(new EditableRaiseEvent(this._raiseEvents, wire));
      }
      this.childNodes = loaded.child_nodes;
      this.childEdges = loaded.child_edges;
      this.locks = loaded.locks;
      this._handles.clear();
      // Opaque `unknown[]` at the API boundary (the backend stores handles as verbatim JSON); the editor's Handle shape is a
      // frontend convention, so narrow here.
      for (const wire of loaded.handles as readonly Handle[]) {
        this._handles.append(new EditableHandle(this._handles, wire));
      }
      this._retentionPolicies.clear();
      for (const wire of loaded.retention_policies) {
        this._retentionPolicies.append(new EditableRetentionPolicy(this._retentionPolicies, wire));
      }
      this._outputMatchers.clear();
      for (const wire of loaded.output_matchers) {
        this._outputMatchers.append(new EditableOutputMatcher(this._outputMatchers, wire));
      }
    } else {
      this._original = null;
      this.name = null;
      this.description = null;
      this.type = null;
      this.subType = null;
      this._zoneName = null;
      this.folderPath = null;
      this.defaultQueuePath = null;
      this.documentation = null;
      this._editableHandles = false;
      this.restrictToSchedulingWindow = null;
      this.autoRestartEnabled = false;
      this.autoRestartAmount = 0;
      this.autoRestartDelaySeconds = 0;
      this.restartOnReturnCodes = [];
      this.holdOnSubmit = false;
      this.runtimeLimitMinHhmmss = null;
      this.runtimeLimitMaxHhmmss = null;
      this.runtimeLimitEvent = null;
      this.runtimeLimitTerminate = false;
      this.config = {};
      this._originalParameterNames = [];
      this._parameters.clear();
      this._waitEvents.clear();
      this._raiseEvents.clear();
      this.childNodes = [];
      this.childEdges = [];
      this.locks = [];
      this._handles.clear();
      this._retentionPolicies.clear();
      this._outputMatchers.clear();
    }
    this._snapshot = this._persistedSnapshot();
  });
}
